import React, { useState, useEffect } from 'react';
import { FiX, FiDownload } from 'react-icons/fi';
import { IoShareOutline } from 'react-icons/io5';
import { isStandalone as detectStandalone } from '@/modules/auth/utils/isStandalone';

function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandaloneMode = detectStandalone();
    setIsStandalone(isStandaloneMode);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if banner was previously dismissed
    const wasDismissed = localStorage.getItem('pwa-install-banner-dismissed') === 'true';
    setDismissed(wasDismissed);

    // Don't show if already installed or dismissed
    if (isStandaloneMode || wasDismissed) {
      return;
    }

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    // For iOS, show banner after a delay if not dismissed
    if (iOS && !wasDismissed) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 3000); // Show after 3 seconds
      return () => clearTimeout(timer);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowBanner(false);
      localStorage.setItem('pwa-install-banner-dismissed', 'true');
    }
    
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-banner-dismissed', 'true');
  };

  // Don't show if already installed, dismissed, or banner shouldn't be shown
  if (isStandalone || dismissed || !showBanner) {
    return null;
  }

  // iOS-specific banner
  if (isIOS) {
    return (
      <div className="bg-gradient-to-r from-brand-600 to-brand-700 text-white p-3 relative">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-3 flex-1">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                Install Football Subs for quick access
              </p>
              <p className="text-xs opacity-90 mt-0.5">
                Tap <span className="inline-flex items-center justify-center w-5 h-5 bg-white/20 rounded mx-1">
                  <IoShareOutline className="w-3 h-3" />
                </span> then "Add to Home Screen"
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 ml-2 p-2.5 hover:bg-white/20 rounded-xl transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 cursor-pointer"
            aria-label="Dismiss"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Android/Chrome banner
  return (
    <div className="bg-gradient-to-r from-brand-600 to-brand-700 text-white p-3 relative">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-3 flex-1">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <FiDownload className="w-5 h-5" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">
              Install Football Subs for quick access
            </p>
            <p className="text-xs opacity-90 mt-0.5">
              Get the app on your home screen for faster access
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleInstallClick}
            className="px-4 py-1.5 bg-white text-brand-700 rounded-lg border border-white font-medium text-sm hover:bg-brand-50 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 flex items-center space-x-1 cursor-pointer"
          >
            <FiDownload className="w-4 h-4" />
            <span>Install</span>
          </button>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-2.5 hover:bg-white/20 rounded-xl transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 cursor-pointer"
            aria-label="Dismiss"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PWAInstallBanner;
